import { useState, useRef, useEffect } from 'react';
import { createClient } from '@blinkdotnew/sdk';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Mic, MicOff, Loader2, Sparkles, User, Mail, Building, FileText, Save, Trash2 } from 'lucide-react';
import { journalSyncQueue } from '@/services/journalSyncQueue';

const blink = createClient({
  projectId: 'lemon-ui-agentic-email-ai-agent-ben3ou87',
  authRequired: false,
  auth: { mode: 'headless' }
});

interface Contact {
  id: string;
  email: string;
  name?: string;
  company?: string;
}

interface VoiceJournalModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  contacts: Contact[];
  onNoteCreated?: (optimisticJournal: {
    id: string;
    userId: string;
    personId: string;
    contactEmail: string;
    title: string;
    content: string;
    insights: string;
    recordedAt: string;
  }) => void;
}

interface MentionedContact {
  contact: Contact;
  mentioned: boolean;
  confidence: number;
}

interface VoiceAction {
  type: 'create_contact' | 'create_meeting' | 'send_email' | 'create_task' | 'create_note';
  data: any;
  description: string;
}

interface ProcessedNote {
  contactEmail?: string;
  mentionedContacts?: string[]; // All contacts mentioned in the journal
  summary: string;
  keyInsights: string[];
  sentiment: 'positive' | 'neutral' | 'negative';
  suggestedTags: string[];
  actions: VoiceAction[];
}

export default function VoiceJournalModal({ 
  isOpen, 
  onClose, 
  userId,
  contacts,
  onNoteCreated 
}: VoiceJournalModalProps) {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [transcribedText, setTranscribedText] = useState('');
  const [processedNote, setProcessedNote] = useState<ProcessedNote | null>(null);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [selectedContacts, setSelectedContacts] = useState<Contact[]>([]); // Support multiple contacts
  const [customNote, setCustomNote] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [editableActions, setEditableActions] = useState<VoiceAction[]>([]);
  // Track all recordings in a session
  const [allTranscriptions, setAllTranscriptions] = useState<string[]>([]);
  
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  useEffect(() => {
    // Auto-select contact when AI identifies one
    if (processedNote?.contactEmail) {
      const contact = contacts.find(c => 
        c.email.toLowerCase() === processedNote.contactEmail?.toLowerCase()
      );
      if (contact) {
        setSelectedContact(contact);
      }
    }

    // Auto-select all mentioned contacts
    if (processedNote?.mentionedContacts && processedNote.mentionedContacts.length > 0) {
      const mentioned = contacts.filter(c =>
        processedNote.mentionedContacts?.some(email => 
          email.toLowerCase() === c.email.toLowerCase()
        )
      );
      setSelectedContacts(mentioned);
    }
  }, [processedNote, contacts]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(chunksRef.current, { type: 'audio/wav' });
        await transcribeAudio(audioBlob);
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      toast.success('Recording started...');
    } catch (error) {
      console.error('Error starting recording:', error);
      toast.error('Could not access microphone');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      toast.info('Processing audio...');
    }
  };

  const transcribeAudio = async (audioBlob: Blob) => {
    setIsProcessing(true);
    try {
      // Convert blob to base64 using FileReader
      const base64Audio = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const dataUrl = reader.result as string;
          const base64Data = dataUrl.split(',')[1]; // Extract base64 part
          resolve(base64Data);
        };
        reader.onerror = reject;
        reader.readAsDataURL(audioBlob);
      });

      // Transcribe using Blink AI
      const { text } = await blink.ai.transcribeAudio({
        audio: base64Audio,
        language: 'en'
      });

      // Append to existing transcriptions
      setAllTranscriptions(prev => [...prev, text]);
      
      // Update transcribedText with all transcriptions combined
      const combinedText = [...allTranscriptions, text].join('\n\n---\n\n');
      setTranscribedText(combinedText);
      
      // Process the combined transcribed text with AI
      await processTranscription(combinedText);
    } catch (error) {
      console.error('Error transcribing audio:', error);
      toast.error('Failed to transcribe audio');
      setIsProcessing(false);
    }
  };

  const processTranscription = async (text: string) => {
    try {
      // Get user's timezone - fallback to UTC if unavailable
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const currentDateTime = new Date().toLocaleString('en-US', { 
        timeZone: userTimezone,
        dateStyle: 'full',
        timeStyle: 'long'
      });
      
      // Use Blink AI with structured tool/action detection
      const { object } = await blink.ai.generateObject({
        prompt: `Analyze this voice journal entry and determine what ACTIONS the user wants to perform:

"${text}"

CURRENT DATE AND TIME (User's timezone: ${userTimezone}): ${currentDateTime}

Available contacts: ${contacts.map(c => `${c.name || c.email} (${c.email})`).join(', ')}

IMPORTANT: IDENTIFY ALL CONTACTS MENTIONED in the transcription, not just one primary contact.

ANALYZE THE INTENT AND EXTRACT ACTIONS:

The user might want to:
1. CREATE A NEW CONTACT - phrases like "add contact", "new contact for", "save contact details"
   - Try to match contact names/emails from: ${contacts.map(c => c.name || c.email).join(', ')}
2. CREATE A MEETING - phrases like "schedule meeting", "set up call", "need to meet" (FUTURE tense only)
   - REQUIRED: Always include a "title" field in meeting data (e.g., "Follow-up with [Name]", "Product Discussion")
   - DEFAULT DURATIONS: Use 15min, 30min, or 60min blocks unless user specifies otherwise
   - Auto-calculate startTime and endTime based on user's timezone (${userTimezone})
3. SEND AN EMAIL - phrases like "send email to", "email them about", "draft message"
4. CREATE A TASK/TODO - phrases like "remind me to", "follow up on", "need to complete"
   - Auto-fill dueDate to 7 days from now if not specified
5. CREATE A NOTE - general conversation notes, interaction summaries

For EACH action identified, create an action object with:
- type: 'create_contact' | 'create_meeting' | 'send_email' | 'create_task' | 'create_note'
- data: structured data for that action type
  * For meetings: MUST include "title", "startTime", "duration" (default: 30), optional "description", "agenda"
  * For tasks: Include "title", "dueDate" (default: +7 days), "priority" (default: medium)
  * For contacts: Match existing contacts from the list above when possible
- description: human-readable summary

ALSO extract general intelligence:
- contactEmail: email of the PRIMARY person discussed - MATCH FROM AVAILABLE CONTACTS: ${contacts.map(c => c.email).join(', ')}
- mentionedContacts: ARRAY of ALL contact emails mentioned in the transcription (e.g., ["john@example.com", "jane@example.com"])
- summary: overall summary of the transcription
- keyInsights: important relationship/conversation insights
- sentiment: positive/neutral/negative
- suggestedTags: categorization tags

IMPORTANT:
- IDENTIFY ALL contacts mentioned in the transcription, not just the primary one
- Store ALL mentioned contacts in mentionedContacts array
- ALWAYS try to match contacts from the available list: ${contacts.map(c => `${c.name || 'Unknown'} (${c.email})`).join(', ')}
- For meetings: ONLY create if FUTURE tense ("need to meet", "schedule") NOT past tense ("met with", "talked to")
- For meetings: ALWAYS include "title" field + default to 30min duration unless specified
- For tasks: Auto-fill dueDate to +7 days from now if not specified
- Use current date/time for relative date interpretation
- Be smart about intent - "follow up with John" = create_task, "schedule call with John" = create_meeting`,
        schema: {
          type: 'object',
          properties: {
            contactEmail: { type: 'string' },
            mentionedContacts: { type: 'array', items: { type: 'string' } },
            summary: { type: 'string' },
            keyInsights: { type: 'array', items: { type: 'string' } },
            sentiment: { type: 'string', enum: ['positive', 'neutral', 'negative'] },
            suggestedTags: { type: 'array', items: { type: 'string' } },
            actions: {
              type: 'array',
              items: {
                type: 'object',
                properties: {
                  type: { 
                    type: 'string', 
                    enum: ['create_contact', 'create_meeting', 'send_email', 'create_task', 'create_note'] 
                  },
                  data: { type: 'object' },
                  description: { type: 'string' }
                },
                required: ['type', 'data', 'description']
              }
            }
          },
          required: ['summary', 'keyInsights', 'sentiment', 'suggestedTags', 'actions']
        }
      });

      // Merge with existing processed note if it exists
      if (processedNote) {
        const mergedContacts = Array.from(new Set([
          ...(processedNote.mentionedContacts || []),
          ...(object.mentionedContacts || [])
        ]));
        const mergedNote: ProcessedNote = {
          contactEmail: object.contactEmail || processedNote.contactEmail,
          mentionedContacts: mergedContacts,
          summary: `${processedNote.summary}\n\n${object.summary}`,
          keyInsights: [...processedNote.keyInsights, ...object.keyInsights],
          sentiment: object.sentiment,
          suggestedTags: Array.from(new Set([...processedNote.suggestedTags, ...object.suggestedTags])),
          actions: [...processedNote.actions, ...object.actions]
        };
        setProcessedNote(mergedNote);
        setCustomNote(prev => prev ? `${prev}\n\n${object.summary}` : object.summary);
      } else {
        setProcessedNote(object as ProcessedNote);
        setCustomNote(object.summary);
      }
      toast.success('AI processing complete!');
    } catch (error) {
      console.error('Error processing transcription:', error);
      toast.error('Failed to process transcription');
    } finally {
      setIsProcessing(false);
    }
  };

  const openConfirmDialog = async () => {
    console.log('🔍 VoiceJournalModal: openConfirmDialog() called', {
      hasCustomNote: !!customNote.trim(),
      hasSelectedContact: !!selectedContact,
      selectedContactId: selectedContact?.id,
      selectedContactEmail: selectedContact?.email
    });
    
    if (!customNote.trim()) {
      console.warn('⚠️ VoiceJournalModal: No note content, aborting');
      toast.error('Please add a note');
      return;
    }

    // If no contact is selected, check if we can create one from the processed note
    if (!selectedContact) {
      console.log('🔍 VoiceJournalModal: No contact selected, attempting to auto-select or create');
      if (processedNote?.contactEmail) {
        // Try to find contact by email
        const existingContact = contacts.find(c => 
          c.email.toLowerCase() === processedNote.contactEmail?.toLowerCase()
        );

        if (existingContact) {
          setSelectedContact(existingContact);
        } else {
          // Create a new contact
          try {
            const now = new Date().toISOString();
            const newContactId = `contact_${Date.now()}`;
            
            // Try to extract name from transcription or email
            let contactName = '';
            
            // Simple name extraction from transcription (look for common patterns)
            const transcriptionLower = transcribedText.toLowerCase();
            const namePatterns = [
              /(?:met with|talked to|spoke with|meeting with|call with)\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)/i,
              /([A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)\s+(?:and I|told me|said|mentioned)/i
            ];
            
            for (const pattern of namePatterns) {
              const match = transcribedText.match(pattern);
              if (match && match[1]) {
                contactName = match[1].trim();
                break;
              }
            }
            
            // Fallback: extract from email
            if (!contactName) {
              const emailName = processedNote.contactEmail.split('@')[0];
              contactName = emailName.charAt(0).toUpperCase() + emailName.slice(1).replace(/[._-]/g, ' ');
            }

            await blink.db.contacts.create({
              id: newContactId,
              userId,
              email: processedNote.contactEmail,
              name: contactName,
              relationshipType: 'professional',
              relationshipStage: 'new',
              engagementLevel: 'active',
              totalInteractions: 1,
              lastInteractionAt: now,
              createdAt: now,
              updatedAt: now
            });

            const newContact = {
              id: newContactId,
              email: processedNote.contactEmail,
              name: contactName
            };

            setSelectedContact(newContact);
            toast.success(`Created new contact: ${contactName}`);
          } catch (error) {
            console.error('Error creating contact:', error);
            toast.error('Failed to create contact. Please select one manually.');
            return;
          }
        }
      } else {
        toast.error('Please select a contact or ensure AI detected one');
        return;
      }
    }

    // Initialize editable actions from processed note
    setEditableActions(processedNote?.actions || []);
    setShowConfirmDialog(true);
  };

  const saveNote = async () => {
    console.log('🎯 VoiceJournalModal: saveNote() called');
    
    try {
      // Validate selectedContact before proceeding
      if (!selectedContact || !selectedContact.id || !selectedContact.email) {
        console.error('❌ VoiceJournalModal: Invalid contact', { selectedContact });
        toast.error('Please select a valid contact');
        return;
      }

      const noteId = `note_${Date.now()}`;
      const journalId = `vj_${Date.now()}`;
      const now = new Date().toISOString();

      console.log('📝 VoiceJournalModal: Saving voice journal', {
        journalId,
        userId,
        personId: selectedContact.id,
        contactEmail: selectedContact.email,
        contentLength: customNote.length
      });

      // 1. Save to voice_journals table (PRIMARY record)
      try {
        await blink.db.voiceJournals.create({
          id: journalId,
          userId,
          personId: selectedContact.id,
          contactEmail: selectedContact.email,
          title: `Voice Note: ${selectedContact.name || selectedContact.email}`,
          content: customNote,
          insights: processedNote?.summary || '',
          recordedAt: now,
          createdAt: now,
          updatedAt: now
        });
        console.log('✅ VoiceJournalModal: Voice journal saved to database', { journalId });
      } catch (dbError) {
        console.error('❌ VoiceJournalModal: Failed to save to voice_journals table', dbError);
        throw dbError;
      }

      // 2. Also save as calendar event with type 'note' for backup
      try {
        await blink.db.calendarEvents.create({
          id: noteId,
          userId,
          title: `Note: ${selectedContact.name || selectedContact.email}`,
          description: customNote,
          startTime: now,
          endTime: now,
          eventType: 'note',
          attendees: selectedContact.email,
          aiSummary: processedNote?.summary,
          createdAt: now,
          updatedAt: now
        });
        console.log('✅ VoiceJournalModal: Backup calendar event created', { noteId });
      } catch (backupError) {
        console.warn('⚠️ VoiceJournalModal: Failed to create backup calendar event (non-critical)', backupError);
        // Non-critical - continue
      }

      // Execute all actions
      let actionsExecuted = 0;
      const executionResults: string[] = [];

      for (const action of editableActions) {
        try {
          switch (action.type) {
            case 'create_contact': {
              const contactData = action.data as any;
              const newContactId = `contact_${Date.now()}_${actionsExecuted}`;
              await blink.db.contacts.create({
                id: newContactId,
                userId,
                email: contactData.email || `${contactData.name?.toLowerCase().replace(/\s+/g, '.')}@contact.ai`,
                name: contactData.name,
                company: contactData.company,
                relationshipType: contactData.relationshipType || 'professional',
                relationshipStage: 'new',
                engagementLevel: 'active',
                totalInteractions: 1,
                lastInteractionAt: now,
                createdAt: now,
                updatedAt: now
              });
              executionResults.push(`✓ Contact created: ${contactData.name}`);
              break;
            }
            case 'create_meeting': {
              const meetingData = action.data as any;
              
              // Auto-select smart meeting duration: 15min, 30min, or 60min
              let meetingDuration = meetingData.duration || 30; // Default to 30min
              
              // If user didn't specify duration, intelligently choose based on meeting type
              if (!meetingData.duration) {
                const descriptionLower = (meetingData.description || meetingData.title || '').toLowerCase();
                if (descriptionLower.includes('quick') || descriptionLower.includes('brief') || descriptionLower.includes('check-in')) {
                  meetingDuration = 15;
                } else if (descriptionLower.includes('detailed') || descriptionLower.includes('review') || descriptionLower.includes('planning')) {
                  meetingDuration = 60;
                } else {
                  meetingDuration = 30; // Default for most meetings
                }
              }
              
              // Parse start time with fallback to "next business day at 10 AM" if not specified
              let meetingStartTime: string;
              if (meetingData.startTime) {
                meetingStartTime = new Date(meetingData.startTime).toISOString();
              } else {
                // Default to next business day at 10:00 AM
                const tomorrow = new Date();
                tomorrow.setDate(tomorrow.getDate() + 1);
                tomorrow.setHours(10, 0, 0, 0);
                // Skip weekends
                while (tomorrow.getDay() === 0 || tomorrow.getDay() === 6) {
                  tomorrow.setDate(tomorrow.getDate() + 1);
                }
                meetingStartTime = tomorrow.toISOString();
              }
              
              const meetingEndTime = new Date(new Date(meetingStartTime).getTime() + meetingDuration * 60000).toISOString();
              
              // Generate auto-agenda if not provided
              const autoAgenda = meetingData.agenda || `• Review meeting objectives\n• Discuss key topics\n• Action items and next steps`;
              
              // Ensure title is always provided (required field)
              const meetingTitle = meetingData.title || 
                                   meetingData.description || 
                                   `Meeting with ${selectedContact.name || selectedContact.email}`;
              
              await blink.db.calendarEvents.create({
                id: `meeting_${Date.now()}_${actionsExecuted}`,
                userId,
                title: meetingTitle,
                description: meetingData.description || `Follow-up meeting with ${selectedContact.name || selectedContact.email}`,
                startTime: meetingStartTime,
                endTime: meetingEndTime,
                eventType: 'meeting',
                attendees: selectedContact.email,
                agenda: autoAgenda,
                aiSummary: `Auto-created from voice note: ${customNote}`,
                createdAt: now,
                updatedAt: now
              });
              executionResults.push(`✓ Meeting scheduled: ${meetingTitle} (${meetingDuration}min)`);
              break;
            }
            case 'send_email': {
              const emailData = action.data as any;
              // Note: Actual email sending would require email API integration
              // For now, create a draft task
              await blink.db.tasks.create({
                id: `task_${Date.now()}_${actionsExecuted}`,
                userId,
                contactId: selectedContact.id,
                title: `📧 Draft email: ${emailData.subject || 'No subject'}`,
                description: emailData.body || emailData.message || 'Email content from voice note',
                priority: 'medium',
                status: 'pending',
                source: 'voice_journal',
                sourceId: noteId,
                assignedBy: 'ai',
                tags: 'email,draft',
                createdAt: now,
                updatedAt: now
              });
              executionResults.push(`✓ Email draft created: ${emailData.subject || 'No subject'}`);
              break;
            }
            case 'create_task': {
              const taskData = action.data as any;
              
              // Auto-fill due date to 7 days from now if not specified
              let dueDate = taskData.dueDate;
              if (!dueDate) {
                const sevenDaysFromNow = new Date();
                sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7);
                dueDate = sevenDaysFromNow.toISOString();
              }
              
              await blink.db.tasks.create({
                id: `task_${Date.now()}_${actionsExecuted}`,
                userId,
                contactId: selectedContact.id,
                title: taskData.title || taskData.task,
                description: taskData.description || `From voice note: ${customNote}`,
                priority: taskData.priority || 'medium',
                dueDate: dueDate,
                status: 'pending',
                source: 'voice_journal',
                sourceId: noteId,
                assignedBy: 'ai',
                tags: processedNote?.suggestedTags.join(',') || '',
                createdAt: now,
                updatedAt: now
              });
              
              const dueDateFormatted = new Date(dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
              executionResults.push(`✓ Task created: ${taskData.title || taskData.task} (due ${dueDateFormatted})`);
              break;
            }
            case 'create_note': {
              // Note is already being created as the main calendar event
              executionResults.push(`✓ Note saved`);
              break;
            }
          }
          actionsExecuted++;
        } catch (error) {
          console.error(`Error executing action ${action.type}:`, error);
          executionResults.push(`✗ Failed: ${action.description}`);
        }
      }

      // Show execution summary
      if (executionResults.length > 0) {
        toast.success(
          <div>
            <p className="font-semibold">Actions Executed:</p>
            {executionResults.map((result, i) => (
              <p key={i} className="text-xs">{result}</p>
            ))}
          </div>,
          { duration: 5000 }
        );
      }

      // Update contact intelligence with new insights - using correct column names with proper validation
      if (processedNote && selectedContact && selectedContact.email) {
        try {
          // Validate contact email format
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(selectedContact.email)) {
            console.warn('Invalid email format, skipping contact intelligence update:', selectedContact.email);
          } else {
            // Fetch existing intelligence records safely - use personId AND userId for lookup
            const existingIntel = await blink.db.contactIntelligenceUnified.list({
              where: {
                personId: selectedContact.id,
                userId
              }
            });

            const actionsSummary = editableActions.map(a => `${a.type}: ${a.description}`).join('; ');

            // Prepare clean data
            const intelData = {
              personId: selectedContact.id,
              userId,
              contactEmail: selectedContact.email.toLowerCase().trim(),
              name: selectedContact.name || null,
              company: selectedContact.company || null,
              behavioralInsights: processedNote.keyInsights.join('. '),
              timelineInsights: `Voice note: ${processedNote.summary}\nActions: ${actionsSummary}`,
              confidenceScore: 85,
              updatedAt: now
            };

            if (existingIntel && existingIntel.length > 0) {
              // Update existing record - only if it exists
              const existingRecord = existingIntel[0];
              await blink.db.contactIntelligenceUnified.update(existingRecord.id, {
                behavioralInsights: intelData.behavioralInsights,
                timelineInsights: intelData.timelineInsights,
                confidenceScore: intelData.confidenceScore,
                name: intelData.name,
                company: intelData.company,
                updatedAt: now
              });
              console.log('✅ Contact intelligence updated successfully');
            } else {
              // Create new record only if none exists - use personId in unique ID generation
              await blink.db.contactIntelligenceUnified.create({
                id: `intel_${userId}_${selectedContact.id}_${Date.now()}`,
                totalInteractions: 1,
                overallScore: 70,
                ...intelData,
                createdAt: now
              });
              console.log('✅ Contact intelligence created successfully');
            }
          }
        } catch (error) {
          console.error('Failed to update contact intelligence:', error);
          // Non-blocking error - don't stop the save process
          toast.warning('Note saved, but contact intelligence update had an issue');
        }
      }

      // Update contact last interaction - with safe fallback
      if (selectedContact && selectedContact.id) {
        try {
          const existingContact = await blink.db.contacts.list({ 
            where: { id: selectedContact.id } 
          });
          const currentInteractions = existingContact[0]?.totalInteractions || 0;
          
          await blink.db.contacts.update(selectedContact.id, {
            lastInteractionAt: now,
            totalInteractions: currentInteractions + 1
          });
        } catch (error) {
          console.error('Error updating contact interactions:', error);
          // Don't fail the save if contact update fails
        }
      }

      // 3. Add to interaction_timeline immediately (don't wait for sync)
      let timelineCreated = false;
      try {
        await blink.db.interactionTimeline.create({
          id: `timeline_journal_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
          userId,
          personId: selectedContact.id,
          interactionType: 'voice_journal',
          interactionDate: now,
          voiceJournalId: journalId,
          channel: 'voice',
          subject: `Voice Note: ${selectedContact.name || selectedContact.email}`,
          snippet: customNote.substring(0, 200),
          content: customNote,
          participants: JSON.stringify([selectedContact.email]),
          outcomes: processedNote?.summary || '',
          createdAt: now,
          updatedAt: now,
        });
        console.log('✅ Voice journal added to timeline immediately');
        timelineCreated = true;
      } catch (timelineError) {
        console.error('Failed to add to timeline:', timelineError);
        // Non-blocking - will be synced later
      }

      // 4. Trigger optimistic UI update BEFORE async operations
      const optimisticJournal = {
        id: journalId,
        userId,
        personId: selectedContact.id,
        contactEmail: selectedContact.email,
        title: `Voice Note: ${selectedContact.name || selectedContact.email}`,
        content: customNote,
        insights: processedNote?.summary || '',
        recordedAt: now,
      };
      
      console.log('📢 VoiceJournalModal: Notifying parent with optimistic journal', { optimisticJournal });
      
      // Notify parent immediately for optimistic UI update
      onNoteCreated?.(optimisticJournal);
      
      console.log('✅ VoiceJournalModal: Parent notified, showing success toast');

      toast.success(`Note saved! Executed ${actionsExecuted} action${actionsExecuted !== 1 ? 's' : ''}`);
      
      // 5. Trigger background timeline sync with retry queue
      if (selectedContact && selectedContact.id && !timelineCreated) {
        // Add to sync queue for reliable background processing with retries
        journalSyncQueue.add({
          id: `sync_${journalId}_${Date.now()}`,
          userId,
          personId: selectedContact.id,
          contactEmail: selectedContact.email,
          journalId,
          createdAt: now,
        });
        
        console.log('📋 Added journal to sync queue for background processing');
        toast.info('Journal will sync in the background', { duration: 2000 });
      } else if (timelineCreated) {
        console.log('✅ Timeline already created, no need for background sync');
      }
      
      console.log('🎉 VoiceJournalModal: Save complete, closing dialogs');
      setShowConfirmDialog(false);
      handleClose();
    } catch (error) {
      console.error('❌ VoiceJournalModal: Error saving note:', error);
      console.error('Error details:', {
        errorMessage: error instanceof Error ? error.message : String(error),
        errorStack: error instanceof Error ? error.stack : undefined,
        selectedContact,
        userId,
        customNoteLength: customNote.length
      });
      toast.error(`Failed to save note: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleClose = () => {
    console.log('🔒 VoiceJournalModal: handleClose() called, resetting state');
    // Reset state
    setTranscribedText('');
    setProcessedNote(null);
    setSelectedContact(null);
    setCustomNote('');
    setIsRecording(false);
    setIsProcessing(false);
    setShowConfirmDialog(false);
    setEditableActions([]);
    setAllTranscriptions([]);
    onClose();
    console.log('✅ VoiceJournalModal: Modal closed and state reset');
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            AI Voice Journal
          </DialogTitle>
          <DialogDescription>
            Record a voice note about your contact interactions. AI will automatically transcribe, 
            analyze, and extract insights.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Recording Section */}
          <Card className={isRecording ? 'border-red-500 shadow-lg' : ''}>
            <CardContent className="pt-6">
              <div className="flex flex-col items-center gap-4">
                <Button
                  size="lg"
                  variant={isRecording ? 'destructive' : 'default'}
                  onClick={isRecording ? stopRecording : startRecording}
                  disabled={isProcessing}
                  className="w-32 h-32 rounded-full"
                >
                  {isRecording ? (
                    <div className="flex flex-col items-center gap-2">
                      <MicOff className="w-12 h-12 animate-pulse" />
                      <span className="text-xs">Stop</span>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <Mic className="w-12 h-12" />
                      <span className="text-xs">Record</span>
                    </div>
                  )}
                </Button>
                
                {isRecording && (
                  <div className="flex items-center gap-2 text-red-500 animate-pulse">
                    <div className="w-3 h-3 bg-red-500 rounded-full animate-ping" />
                    <span className="font-medium">Recording...</span>
                  </div>
                )}

                {isProcessing && (
                  <div className="flex items-center gap-2 text-primary">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Processing with AI...</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Transcription Display */}
          {transcribedText && (
            <Card>
              <CardContent className="pt-6">
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <FileText className="w-4 h-4 text-blue-500" />
                    Transcription
                  </div>
                  <div className="p-3 bg-secondary rounded-lg text-sm">
                    {transcribedText}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* AI Analysis Results */}
          {processedNote && (
            <>
              <Card className="border-primary/50">
                <CardContent className="pt-6">
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <Sparkles className="w-4 h-4 text-primary" />
                      AI Analysis
                    </div>

                    {/* Sentiment Badge */}
                    <div>
                      <Badge 
                        variant={
                          processedNote.sentiment === 'positive' ? 'default' :
                          processedNote.sentiment === 'negative' ? 'destructive' :
                          'secondary'
                        }
                      >
                        {processedNote.sentiment.charAt(0).toUpperCase() + processedNote.sentiment.slice(1)} Sentiment
                      </Badge>
                    </div>

                    {/* Key Insights */}
                    {processedNote.keyInsights.length > 0 && (
                      <div>
                        <p className="text-sm font-medium mb-2">Key Insights:</p>
                        <ul className="space-y-1">
                          {processedNote.keyInsights.map((insight, i) => (
                            <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                              <span className="text-primary">•</span>
                              <span>{insight}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Detected Actions */}
                    {processedNote.actions && processedNote.actions.length > 0 && (
                      <div>
                        <p className="text-sm font-medium mb-2">Detected Actions ({processedNote.actions.length}):</p>
                        <ul className="space-y-2">
                          {processedNote.actions.map((action, i) => {
                            const actionIcons = {
                              create_contact: '👤',
                              create_meeting: '📅',
                              send_email: '📧',
                              create_task: '✓',
                              create_note: '📝'
                            };
                            const actionColors = {
                              create_contact: 'bg-green-500/10 text-green-700',
                              create_meeting: 'bg-blue-500/10 text-blue-700',
                              send_email: 'bg-purple-500/10 text-purple-700',
                              create_task: 'bg-orange-500/10 text-orange-700',
                              create_note: 'bg-gray-500/10 text-gray-700'
                            };
                            
                            return (
                              <li key={i} className={`text-sm flex items-start gap-2 p-2 rounded-md ${actionColors[action.type]}`}>
                                <span className="text-lg">{actionIcons[action.type]}</span>
                                <div className="flex-1">
                                  <p className="font-medium">{action.description}</p>
                                  <Badge variant="outline" className="text-xs mt-1">
                                    {action.type.replace(/_/g, ' ')}
                                  </Badge>
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}

                    {/* Suggested Tags */}
                    {processedNote.suggestedTags.length > 0 && (
                      <div>
                        <p className="text-sm font-medium mb-2">Tags:</p>
                        <div className="flex flex-wrap gap-2">
                          {processedNote.suggestedTags.map((tag, i) => (
                            <Badge key={i} variant="outline" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Contact Selection */}
              <Card>
                <CardContent className="pt-6">
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <User className="w-4 h-4 text-green-500" />
                      Associate with Contact
                    </div>
                    
                    {selectedContact ? (
                      <div className="p-3 bg-secondary rounded-lg">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                              <span className="text-sm font-medium text-primary">
                                {selectedContact.name?.charAt(0) || selectedContact.email.charAt(0)}
                              </span>
                            </div>
                            <div>
                              <p className="font-medium">{selectedContact.name || selectedContact.email}</p>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <Mail className="w-3 h-3" />
                                {selectedContact.email}
                              </div>
                              {selectedContact.company && (
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                  <Building className="w-3 h-3" />
                                  {selectedContact.company}
                                </div>
                              )}
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectedContact(null)}
                          >
                            Change
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <p className="text-sm text-muted-foreground">Select a contact:</p>
                        <div className="max-h-40 overflow-y-auto space-y-1">
                          {contacts.map((contact) => (
                            <button
                              key={contact.id}
                              onClick={() => setSelectedContact(contact)}
                              className="w-full p-2 text-left rounded-lg hover:bg-secondary transition-colors"
                            >
                              <p className="text-sm font-medium">{contact.name || contact.email}</p>
                              <p className="text-xs text-muted-foreground">{contact.email}</p>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Editable Note */}
              <Card>
                <CardContent className="pt-6">
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <FileText className="w-4 h-4 text-purple-500" />
                      Note (Editable)
                    </div>
                    <Textarea
                      value={customNote}
                      onChange={(e) => setCustomNote(e.target.value)}
                      rows={6}
                      placeholder="Edit the AI-generated note or write your own..."
                      className="resize-none"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Save Buttons */}
              <div className="flex gap-3">
                <Button 
                  onClick={openConfirmDialog} 
                  className="flex-1"
                  size="lg"
                  disabled={!selectedContact || !customNote.trim()}
                >
                  <Save className="w-4 h-4 mr-2" />
                  Review & Save
                </Button>
                <Button 
                  onClick={async () => {
                    // Quick save without review dialog
                    if (!selectedContact || !customNote.trim()) {
                      toast.error('Please select a contact and add a note');
                      return;
                    }
                    await saveNote();
                  }}
                  variant="outline"
                  size="lg"
                  disabled={!selectedContact || !customNote.trim()}
                >
                  Quick Save
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              Review Before Saving
            </DialogTitle>
            <DialogDescription>
              Review and edit the extracted tasks and meetings before saving. You can modify, remove, or keep items as-is.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* AI Analysis in Editable Note */}
            <Card>
              <CardContent className="pt-6">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <Sparkles className="w-4 h-4 text-primary" />
                      Note & AI Analysis
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        // Add AI analysis to the note
                        if (processedNote) {
                          const analysisText = `
=== AI ANALYSIS ===

Sentiment: ${processedNote.sentiment.charAt(0).toUpperCase() + processedNote.sentiment.slice(1)}

Key Insights:
${processedNote.keyInsights.map((insight, i) => `${i + 1}. ${insight}`).join('\n')}

Suggested Tags: ${processedNote.suggestedTags.join(', ')}

===================
`;
                          setCustomNote(prev => prev + '\n\n' + analysisText);
                          toast.success('AI analysis added to note');
                        }
                      }}
                      className="h-7 text-xs"
                    >
                      <Sparkles className="w-3 h-3 mr-1" />
                      Add AI Analysis
                    </Button>
                  </div>
                  <Textarea
                    value={customNote}
                    onChange={(e) => setCustomNote(e.target.value)}
                    rows={10}
                    placeholder="Edit the AI-generated analysis and notes before saving..."
                    className="resize-none"
                  />
                  <p className="text-xs text-muted-foreground">
                    💡 Tip: Click "Add AI Analysis" to append the full AI insights to your note, or edit directly to customize.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Actions Section */}
            {editableActions.length > 0 && (
              <Card>
                <CardContent className="pt-6">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-sm flex items-center gap-2">
                        <Badge variant="default" className="text-xs">
                          {editableActions.length}
                        </Badge>
                        Actions to Execute
                      </h3>
                    </div>

                    {editableActions.map((action, index) => {
                      const actionIcons = {
                        create_contact: '👤',
                        create_meeting: '📅',
                        send_email: '📧',
                        create_task: '✓',
                        create_note: '📝'
                      };

                      return (
                        <div key={index} className="p-3 bg-secondary/50 rounded-lg space-y-3 border">
                          <div className="flex items-start gap-2">
                            <span className="text-xl">{actionIcons[action.type]}</span>
                            <div className="flex-1 space-y-2">
                              <div className="flex items-center justify-between">
                                <Badge variant="outline" className="text-xs">
                                  {action.type.replace(/_/g, ' ')}
                                </Badge>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setEditableActions(editableActions.filter((_, i) => i !== index));
                                  }}
                                  className="text-destructive h-6 px-2"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                              
                              <Input
                                value={action.description}
                                onChange={(e) => {
                                  const updated = [...editableActions];
                                  updated[index].description = e.target.value;
                                  setEditableActions(updated);
                                }}
                                placeholder="Action description"
                                className="text-sm"
                              />

                              {/* Action-specific fields */}
                              {action.type === 'create_task' && (
                                <div className="grid grid-cols-2 gap-2">
                                  <div className="space-y-1">
                                    <Label className="text-xs">Priority</Label>
                                    <select
                                      value={action.data.priority || 'medium'}
                                      onChange={(e) => {
                                        const updated = [...editableActions];
                                        updated[index].data = { ...action.data, priority: e.target.value };
                                        setEditableActions(updated);
                                      }}
                                      className="w-full p-2 text-xs rounded-md border bg-background"
                                    >
                                      <option value="low">Low</option>
                                      <option value="medium">Medium</option>
                                      <option value="high">High</option>
                                    </select>
                                  </div>
                                  <div className="space-y-1">
                                    <Label className="text-xs">Due Date</Label>
                                    <Input
                                      type="date"
                                      value={action.data.dueDate ? new Date(action.data.dueDate).toISOString().split('T')[0] : ''}
                                      onChange={(e) => {
                                        const updated = [...editableActions];
                                        updated[index].data = { ...action.data, dueDate: e.target.value ? new Date(e.target.value).toISOString() : undefined };
                                        setEditableActions(updated);
                                      }}
                                      className="text-xs"
                                    />
                                  </div>
                                </div>
                              )}

                              {action.type === 'create_meeting' && (
                                <div className="space-y-2">
                                  <div className="grid grid-cols-2 gap-2">
                                    <div className="space-y-1">
                                      <Label className="text-xs">Start Time</Label>
                                      <Input
                                        type="datetime-local"
                                        value={action.data.startTime ? new Date(action.data.startTime).toISOString().slice(0, 16) : ''}
                                        onChange={(e) => {
                                          const updated = [...editableActions];
                                          updated[index].data = { ...action.data, startTime: new Date(e.target.value).toISOString() };
                                          setEditableActions(updated);
                                        }}
                                        className="text-xs"
                                      />
                                    </div>
                                    <div className="space-y-1">
                                      <Label className="text-xs">Duration</Label>
                                      <select
                                        value={action.data.duration || 30}
                                        onChange={(e) => {
                                          const updated = [...editableActions];
                                          updated[index].data = { ...action.data, duration: parseInt(e.target.value) };
                                          setEditableActions(updated);
                                        }}
                                        className="w-full p-2 text-xs rounded-md border bg-background"
                                      >
                                        <option value="15">15 minutes</option>
                                        <option value="30">30 minutes</option>
                                        <option value="60">1 hour</option>
                                        <option value="90">1.5 hours</option>
                                        <option value="120">2 hours</option>
                                      </select>
                                    </div>
                                  </div>
                                  <div className="space-y-1">
                                    <Label className="text-xs">Agenda (editable)</Label>
                                    <Textarea
                                      value={action.data.agenda || '• Review meeting objectives\n• Discuss key topics\n• Action items and next steps'}
                                      onChange={(e) => {
                                        const updated = [...editableActions];
                                        updated[index].data = { ...action.data, agenda: e.target.value };
                                        setEditableActions(updated);
                                      }}
                                      rows={4}
                                      placeholder="Meeting agenda..."
                                      className="text-xs"
                                    />
                                  </div>
                                </div>
                              )}

                              {action.type === 'create_contact' && (
                                <div className="grid grid-cols-2 gap-2">
                                  <div className="space-y-1">
                                    <Label className="text-xs">Name</Label>
                                    <Input
                                      value={action.data.name || ''}
                                      onChange={(e) => {
                                        const updated = [...editableActions];
                                        updated[index].data = { ...action.data, name: e.target.value };
                                        setEditableActions(updated);
                                      }}
                                      placeholder="Contact name"
                                      className="text-xs"
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    <Label className="text-xs">Email</Label>
                                    <Input
                                      value={action.data.email || ''}
                                      onChange={(e) => {
                                        const updated = [...editableActions];
                                        updated[index].data = { ...action.data, email: e.target.value };
                                        setEditableActions(updated);
                                      }}
                                      placeholder="contact@example.com"
                                      className="text-xs"
                                    />
                                  </div>
                                </div>
                              )}

                              {action.type === 'send_email' && (
                                <div className="space-y-2">
                                  <div className="space-y-1">
                                    <Label className="text-xs">Subject</Label>
                                    <Input
                                      value={action.data.subject || ''}
                                      onChange={(e) => {
                                        const updated = [...editableActions];
                                        updated[index].data = { ...action.data, subject: e.target.value };
                                        setEditableActions(updated);
                                      }}
                                      placeholder="Email subject"
                                      className="text-xs"
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    <Label className="text-xs">Message</Label>
                                    <Textarea
                                      value={action.data.body || action.data.message || ''}
                                      onChange={(e) => {
                                        const updated = [...editableActions];
                                        updated[index].data = { ...action.data, body: e.target.value };
                                        setEditableActions(updated);
                                      }}
                                      rows={3}
                                      placeholder="Email content"
                                      className="text-xs"
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* No actions message */}
            {editableActions.length === 0 && (
              <Card>
                <CardContent className="pt-6 text-center py-8">
                  <p className="text-muted-foreground">
                    No actions detected. The note will be saved with your contact intelligence.
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Summary */}
            <Card>
              <CardContent className="pt-6">
                <div className="text-sm space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Actions to execute:</span>
                    <Badge variant="secondary">{editableActions.length}</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Contact:</span>
                    <Badge variant="outline">{selectedContact?.name || selectedContact?.email}</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Note length:</span>
                    <Badge variant="outline">{customNote.length} chars</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={() => setShowConfirmDialog(false)}
                className="flex-1"
              >
                Back to Edit
              </Button>
              <Button 
                onClick={saveNote}
                className="flex-1"
              >
                <Save className="w-4 h-4 mr-2" />
                Confirm & Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
